import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Table } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/form/Input';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Search } from '@/components/icons';
import { useToast } from '@/components/ui/useToast';
import {
  listSisCommission,
  saveSisCommission,
  type SisCommissionRow,
} from '@/api/sisCommission';

type Edit = { commissionPct: string; settlementDays: string; notes: string };

export function SisCommissionPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const query = useQuery({ queryKey: ['sis-commission'], queryFn: () => listSisCommission() });
  const rows = query.data ?? [];
  const [edits, setEdits] = useState<Record<string, Edit>>({});
  const [search, setSearch] = useState('');

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.sisGroup.toLowerCase().includes(q) || (r.notes ?? '').toLowerCase().includes(q));
  }, [rows, search]);

  useEffect(() => {
    if (!query.data) return;
    const m: Record<string, Edit> = {};
    for (const r of query.data)
      m[r.sisGroup] = {
        commissionPct: String(r.commissionPct),
        settlementDays: r.settlementDays != null ? String(r.settlementDays) : '',
        notes: r.notes,
      };
    setEdits(m);
  }, [query.data]);

  const dirty = useMemo(
    () =>
      rows.some((r) => {
        const e = edits[r.sisGroup];
        if (!e) return false;
        const days = e.settlementDays === '' ? null : Number(e.settlementDays);
        return (
          Number(e.commissionPct || 0) !== r.commissionPct ||
          days !== r.settlementDays ||
          (e.notes ?? '') !== (r.notes ?? '')
        );
      }),
    [rows, edits],
  );

  const save = useMutation({
    mutationFn: () =>
      saveSisCommission(
        rows.map((r) => ({
          sisGroup: r.sisGroup,
          commissionPct: Number(edits[r.sisGroup]?.commissionPct || 0),
          settlementDays:
            edits[r.sisGroup]?.settlementDays === '' || edits[r.sisGroup]?.settlementDays == null
              ? undefined
              : Number(edits[r.sisGroup].settlementDays),
          notes: edits[r.sisGroup]?.notes || undefined,
        })),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sis-commission'] });
      toast.push('Commission rates saved', 'success');
    },
    onError: () => toast.push('Could not save commission rates', 'error'),
  });

  const set = (group: string, patch: Partial<Edit>) =>
    setEdits((e) => ({ ...e, [group]: { ...e[group], ...patch } }));

  return (
    <div>
      <SectionHeader
        title="Commission & Settlement"      />

      <FilterBar>
        <FilterField label="Search" className="sm:w-[300px]">
          <div className="relative">
            <Search size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search group or notes" className="!pl-8" />
          </div>
        </FilterField>
        <Button variant="primary" size="sm" className="sm:ml-auto" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </FilterBar>

      <Table<SisCommissionRow>
        storageKey="sis-commission"
        title="Groups"
        count={`${filteredRows.length} groups`}
        rows={filteredRows}
        rowKey={(r) => r.sisGroup}
        loading={query.isFetching && rows.length === 0}
        columns={[
          { key: 'group', header: 'Group', width: '220px', cell: (r) => <strong>{r.sisGroup}</strong> },
          { key: 'stores', header: 'Stores', width: '90px', align: 'right', cell: (r) => r.storeCount },
          {
            key: 'status',
            header: 'Status',
            width: '110px',
            align: 'center',
            cell: (r) =>
              r.isSet ? <Badge tone="success">Set</Badge> : <Badge tone="warning">Not set</Badge>,
          },
          {
            key: 'pct',
            header: 'Commission %',
            width: '160px',
            align: 'right',
            cell: (r) => (
              <div className="flex items-center justify-end gap-1">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step="0.01"
                  value={edits[r.sisGroup]?.commissionPct ?? ''}
                  onChange={(e) => set(r.sisGroup, { commissionPct: e.target.value })}
                  className="w-24 text-right"
                  placeholder="0"
                />
                <span className="text-[var(--text-muted)]">%</span>
              </div>
            ),
          },
          {
            key: 'settle',
            header: 'Settlement (days)',
            width: '150px',
            align: 'right',
            cell: (r) => (
              <Input
                type="number"
                min={0}
                max={365}
                value={edits[r.sisGroup]?.settlementDays ?? ''}
                onChange={(e) => set(r.sisGroup, { settlementDays: e.target.value })}
                className="w-24 text-right"
                placeholder="e.g. 15"
              />
            ),
          },
          {
            key: 'notes',
            header: 'Notes',
            cell: (r) => (
              <Input
                value={edits[r.sisGroup]?.notes ?? ''}
                onChange={(e) => set(r.sisGroup, { notes: e.target.value })}
                placeholder="Optional"
              />
            ),
          },
        ]}
      />

      {rows.length === 0 && !query.isFetching && (
        <p className="mt-3 text-sm text-[var(--text-muted)]">
          No SIS groups yet. Assign a group to your SIS stores (Party Master → sisGroup) to set commission here.
        </p>
      )}
    </div>
  );
}
